import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { sendOtp, validateOtp } from "@/lib/api/auth-actions";
import { IOrder, OtpReason } from "muminsmart-utils";
import { toast } from "sonner";

import { normalizePhoneNumber } from "../auth/signup/ContactOtpVerification";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 120;

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

interface PickupOrderOtpDialogProps {
  readonly order: IOrder | null;
  readonly isSubmitting: boolean;
  readonly onClose: () => void;
  readonly onVerified: () => void;
}

function PickupOrderOtpDialog({
  order,
  isSubmitting,
  onClose,
  onVerified,
}: PickupOrderOtpDialogProps) {
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  const customerMobile = useMemo(
    () =>
      order?.user?.phoneNumber
        ? normalizePhoneNumber(order.user.phoneNumber)
        : "",
    [order],
  );
  const hasCustomerMobile = customerMobile.length > 0;

  const resetState = useCallback(() => {
    setOtp("");
    setOtpSent(false);
    setResendSecondsLeft(0);
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
  }, []);

  const handleSendOtp = useCallback(async () => {
    if (!customerMobile) {
      toast.error("Customer phone number is missing.");
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await sendOtp({
        mobile: customerMobile,
        reason: OtpReason.PICKUP_ORDER,
      });

      if (response.success) {
        setOtpSent(true);
        setOtp("");
        setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
        toast.success("OTP sent to customer.");
      } else {
        toast.error(response.message || "Failed to send OTP.");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to send OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  }, [customerMobile]);

  useEffect(() => {
    if (!order) {
      resetState();
      return;
    }

    resetState();
    if (customerMobile) {
      void handleSendOtp();
    } else {
      toast.error("Customer phone number is missing.");
    }
  }, [order, customerMobile, resetState, handleSendOtp]);

  useEffect(() => {
    if (!otpSent || resendSecondsLeft <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, resendSecondsLeft]);

  const handleVerifyOtp = async () => {
    if (!hasCustomerMobile) {
      toast.error("Customer phone number is missing.");
      return;
    }

    if (!otpSent) {
      toast.error("Please send OTP to the customer first.");
      return;
    }

    if (otp.length !== OTP_LENGTH) {
      toast.error(`OTP must be ${OTP_LENGTH} digits.`);
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await validateOtp({
        mobile: customerMobile,
        reason: OtpReason.PICKUP_ORDER,
        otp,
      });

      if (response.success) {
        toast.success("OTP verified. Handing over the order.");
        onVerified();
      } else {
        toast.error(response.message || "Invalid OTP.");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to verify OTP.",
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const resendButtonLabel = (() => {
    if (isSendingOtp) {
      return "Sending...";
    }
    if (resendSecondsLeft > 0) {
      return `Resend in ${formatCountdown(resendSecondsLeft)}`;
    }
    if (otpSent) {
      return "Resend OTP";
    }
    return "Send OTP";
  })();

  return (
    <Dialog
      open={Boolean(order)}
      onOpenChange={(open) => {
        if (!open && !isSubmitting && !isVerifyingOtp) {
          onClose();
        }
      }}
    >
      <DialogContent className="rounded-3xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Verify Customer OTP</DialogTitle>
          <DialogDescription>
            {hasCustomerMobile ? (
              <>
                An OTP has been sent to the customer&apos;s phone (
                {customerMobile}). Ask the customer for the code and enter it
                below to confirm pickup handover for order{" "}
                {order?.orderNumber ?? ""}.
              </>
            ) : (
              <>
                Customer phone number is not available for order{" "}
                {order?.orderNumber ?? ""}. OTP verification cannot proceed.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex justify-center">
            <InputOTP
              maxLength={OTP_LENGTH}
              value={otp}
              onChange={setOtp}
              disabled={!hasCustomerMobile}
            >
              <InputOTPGroup>
                {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                  <InputOTPSlot key={`pickup-otp-${index}`} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => void handleSendOtp()}
              disabled={
                !hasCustomerMobile ||
                isSendingOtp ||
                isSubmitting ||
                isVerifyingOtp ||
                resendSecondsLeft > 0
              }
            >
              {resendButtonLabel}
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={onClose}
            disabled={isSubmitting || isVerifyingOtp}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-full bg-primary"
            onClick={() => void handleVerifyOtp()}
            disabled={
              isSubmitting || isVerifyingOtp || !hasCustomerMobile || !otpSent
            }
          >
            {isSubmitting || isVerifyingOtp
              ? "Verifying..."
              : "Verify & Mark Delivered"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PickupOrderOtpDialog;
